/**
 * @fileoverview Ultra-reactive mobile frontend component for stadium fans playing "Memorama 1 vs 1" (Memory).
 * Full mobile experience implementing:
 * - Character/Side selection mounted on GoalBase with interactive Player 1 vs Player 2 cards.
 * - Waiting room with brand logo, status badge, audio mute toggle, screen WakeLock and proactive image caching.
 * - Arcade countdown overlay (3..2..1) with synchronized synthesizer beeps.
 * - Preview phase (if PreviewTimeSeconds > 0) with all cards revealed for memorization and locked board.
 * - Tactile 3x4 game board with 0ms Optimistic UI 3D flip animation, native Web Audio feedback, haptics,
 *   preemptive locking on 2nd flip, match glow, 800ms failure delay, and move/pair counters.
 * - Full-screen results overlay (#198754 Won, #dc3545 Lost, #0dcaf0 Tie) with WakeLock release and fanfare.
 * - Kicked_out displacement handler and voluntary leave buttons.
 * 
 * @module components/Dashboard/Sections/MemoryPlayer
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoalBase } from '../../Admin/Commons/GoalBase';
import {
    ConnectWebSocketForMemoryFan,
    DisconnectWebSocketForMemoryFan
} from '../../../utilities/WS/MemoryFanWS';
import SignalRService from '../../../utilities/WS/SignalRService';
import {
    GetActiveRoom,
    GetConfig,
    GetMatchState,
    SelectPlayerSide,
    FlipCard,
    LeaveGame,
    isAudioMuted,
    toggleAudioMute,
    setAudioMuted,
    unlockAudioContext,
    playFlipSound,
    playMatchSuccessSound,
    playMatchFailedSound,
    playVictoryFanfare,
    playCountdownBeep,
    hapticFlip,
    hapticMatchSuccess,
    hapticMatchFailed,
    hapticVictory,
    preloadImages,
    getStoredFanPlayer,
    setStoredFanPlayer,
    clearStoredFanPlayer,
    encodeAccessCode,
    decodeAccessCode,
    getMemoryAuth,
    setMemoryAuth,
    clearMemoryAuth
} from '../../../utilities/BL/Memory';

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_THEME = {
    player1Name: 'Jugador 1',
    player1Color: '#fbbf24',
    player2Name: 'Jugador 2',
    player2Color: '#60a5fa',
    fanBackgroundColor: '#0f172a',
    fanTextColor: '#ffffff',
    cardBackBackgroundColor: '#1e293b',
    pairsCount: 6,
    roundDurationSeconds: 60,
    previewTimeSeconds: 3,
    hideUnmatchedDelayMs: 800
};

/**
 * Resolves absolute URL for media assets supporting relative API paths, external CDNs and LAN mobile access.
 * 
 * @param {string|null} mediaPath - Relative path or full URL.
 * @returns {string} Fully resolved media URL.
 */
export const getResolvedMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    let apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/').replace('/api/', '/').replace('/api', '/');
    
    if (typeof window !== 'undefined' && window.location && window.location.hostname && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        if (apiBaseEndpointUrl.includes('localhost') || apiBaseEndpointUrl.includes('127.0.0.1')) {
            apiBaseEndpointUrl = apiBaseEndpointUrl.replace('localhost', window.location.hostname).replace('127.0.0.1', window.location.hostname);
        }
        if (window.location.protocol === 'http:' && apiBaseEndpointUrl.startsWith('https://')) {
            apiBaseEndpointUrl = apiBaseEndpointUrl.replace('https://', 'http://');
        }
    }

    const sanitizedPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
    return `${apiBaseEndpointUrl}${sanitizedPath}`;
};

/**
 * Generates initial empty cards array for the 3x4 grid.
 * 
 * @param {number} totalCards - Total number of cards (default 12 for 6 pairs).
 * @returns {Array<Object>} Initialized card items.
 */
const createInitialCards = (totalCards = 12) => {
    return Array.from({ length: totalCards }, (_, idx) => ({
        cardIndex: idx,
        pairId: null,
        imageUrl: null,
        isFlipped: false,
        isMatched: false
    }));
};

/**
 * MemoryPlayer / MemoryFan Component
 * 
 * @function MemoryPlayer
 * @param {Object} [props] - Component properties.
 * @param {Object} [props.matchState] - Real-time match state from router or parent component.
 * @param {Object} [props.config] - Theme/game configuration.
 * @param {string} [props.winner] - Match winner ('player1' | 'player2' | 'tie').
 * @param {string} [props.roomId] - Room GUID identifier.
 * @param {string} [props.playerSide] - Preset player side ("player1" | "player2").
 * @param {string} [props.side] - Alternative preset player side.
 * @returns {React.ReactElement} The rendered mobile fan interface.
 */
export const MemoryPlayer = ({
    matchState: propMatchState,
    config: propConfig,
    winner: propWinner,
    roomId: propRoomId,
    playerSide: propPlayerSide,
    side: propSide
}) => {
    // =========================================================================
    // 1. SALA Y SESIÓN DE AFICIONADO
    // =========================================================================
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    // Decodificación de token encubierto de acceso (?t=...)
    const tokenParam = searchParams.get('t') || searchParams.get('token') || searchParams.get('k');
    const decodedToken = useMemo(() => {
        return tokenParam ? decodeAccessCode(tokenParam) : null;
    }, [tokenParam]);

    const [activeRoomId, setActiveRoomId] = useState(() => {
        return (
            decodedToken?.roomId ||
            propRoomId ||
            searchParams.get('roomId') ||
            propMatchState?.roomId ||
            (typeof localStorage !== 'undefined' ? localStorage.getItem('active_room_id') : '') ||
            ''
        );
    });

    const [roomAccessPin, setRoomAccessPin] = useState(() => {
        return decodedToken?.pin || propMatchState?.accessPin || propMatchState?.AccessPin || '';
    });

    // Estado de autorización (sessionStorage por 3 horas o QR decodificado)
    const [isAuthorized, setIsAuthorized] = useState(() => {
        if (decodedToken?.roomId) {
            setMemoryAuth(decodedToken.roomId, decodedToken.pin);
            return true;
        }
        const plainPin = searchParams.get('pin');
        if (plainPin) {
            const rId = propRoomId || searchParams.get('roomId') || (typeof localStorage !== 'undefined' ? localStorage.getItem('active_room_id') : '');
            if (rId) {
                setMemoryAuth(rId, plainPin);
                return true;
            }
        }
        const currentStored = getMemoryAuth(decodedToken?.roomId || propRoomId || searchParams.get('roomId'));
        return !!currentStored?.authorized;
    });

    // Auto-limpieza instantánea de la barra de direcciones del navegador en el móvil
    useEffect(() => {
        if ((tokenParam || searchParams.get('pin')) && typeof window !== 'undefined' && window.history?.replaceState) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [tokenParam, searchParams]);

    // Estados del formulario y bloqueo suave por dispositivo (3 intentos -> 30s de pausa)
    const [pinInput, setPinInput] = useState('');
    const [pinError, setPinError] = useState('');
    const [pinFailedAttempts, setPinFailedAttempts] = useState(0);
    const [pinCooldownSeconds, setPinCooldownSeconds] = useState(0);

    useEffect(() => {
        if (pinCooldownSeconds <= 0) return;
        const timer = setInterval(() => {
            setPinCooldownSeconds(prev => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [pinCooldownSeconds]);

    const activeRoomIdRef = useRef(activeRoomId);
    useEffect(() => {
        activeRoomIdRef.current = activeRoomId;
    }, [activeRoomId]);

    // Resolución automática de sala activa si se ingresa a /memory sin parámetros
    useEffect(() => {
        let isMounted = true;
        if (!activeRoomId) {
            GetActiveRoom().then((res) => {
                if (isMounted && res.success && res.data?.roomId) {
                    setActiveRoomId(res.data.roomId);
                    if (typeof localStorage !== 'undefined') {
                        localStorage.setItem('active_room_id', res.data.roomId);
                    }
                }
            }).catch(() => {});
        }
        return () => { isMounted = false; };
    }, [activeRoomId]);

    // Verificación de fondo ante reconexiones: si el PIN del servidor cambió, invalidar sessionStorage
    useEffect(() => {
        let isMounted = true;
        const currentRoom = activeRoomIdRef.current || activeRoomId;
        if (isAuthorized && currentRoom) {
            GetMatchState(currentRoom).then((res) => {
                if (isMounted && res && res.success && res.data) {
                    const serverPin = res.data.accessPin || res.data.AccessPin;
                    if (serverPin) {
                        setRoomAccessPin(String(serverPin).trim());
                        const stored = getMemoryAuth(currentRoom);
                        if (stored?.pin && String(stored.pin).trim() !== String(serverPin).trim()) {
                            clearMemoryAuth(currentRoom);
                            setIsAuthorized(false);
                            setSelectedPlayerSide('');
                        }
                    }
                }
            }).catch(() => {});
        }
        return () => { isMounted = false; };
    }, [activeRoomId, isAuthorized]);

    const initialSession = useMemo(() => {
        return getStoredFanPlayer(activeRoomId) || null;
    }, [activeRoomId]);

    const [selectedPlayerSide, setSelectedPlayerSide] = useState(() => {
        const directProp = propPlayerSide || propSide || propMatchState?.playerSide || propMatchState?.side;
        if (directProp) {
            const raw = String(directProp).toLowerCase();
            return (raw === 'player1' || raw === 'j1' || raw === 'p1' || raw === '1') ? 'player1' : 'player2';
        }
        if (initialSession?.playerChoice) {
            const raw = String(initialSession.playerChoice).toLowerCase();
            return (raw === 'player1' || raw === 'j1' || raw === 'p1' || raw === '1') ? 'player1' : 'player2';
        }
        return '';
    });

    // =========================================================================
    // 2. ESTADOS DE LA PARTIDA Y TEMA VISUAL
    // =========================================================================
    const [enginePhase, setEnginePhase] = useState(() => {
        const rawPhase = propMatchState?.phase || propConfig?.phase || 'idle';
        const p = String(rawPhase).toLowerCase();
        if (p === 'playing' || p === 'running') return 'playing';
        if (p === 'countdown') return 'countdown';
        if (p === 'preview') return 'preview';
        if (p === 'finished' || p === 'game_over') return 'finished';
        if (p === 'game_ready' || p === 'gameready') return 'game_ready';
        if (p === 'cover') return 'cover';
        return 'idle';
    });

    const [countdownValue, setCountdownValue] = useState(3);
    const [previewRemainingSeconds, setPreviewRemainingSeconds] = useState(3);
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [activeTheme, setActiveTheme] = useState(() => propConfig || propMatchState?.theme || DEFAULT_THEME);
    const [matchWinner, setMatchWinner] = useState(() => propWinner || propMatchState?.winner || null);
    const [winningReason, setWinningReason] = useState(() => propMatchState?.winningReason || '');

    // Métricas del jugador y del rival
    const [myScore, setMyScore] = useState(0);
    const [myTotalFlips, setMyTotalFlips] = useState(0);
    const [rivalScore, setRivalScore] = useState(0);
    const [rivalTotalFlips, setRivalTotalFlips] = useState(0);
    const [remainingMatchSeconds, setRemainingMatchSeconds] = useState(60);

    // =========================================================================
    // 3. TABLERO 4 COLUMNAS HOMOLOGADO, BLOQUEO Y TURNOS (OPTIMISTIC UI 0ms)
    // =========================================================================
    const pairsCount = Number(activeTheme?.pairsCount || activeTheme?.PairsCount || 6);
    const totalCardsCount = pairsCount * 2; // 8 (4x2), 12 (4x3) o 16 (4x4)
    const gridClass = pairsCount <= 4 ? 'memory-fan__grid--8-cards' : pairsCount <= 6 ? 'memory-fan__grid--12-cards' : 'memory-fan__grid--16-cards';

    const [cards, setCards] = useState(() => createInitialCards(totalCardsCount));
    const [isBoardLocked, setIsBoardLocked] = useState(false);
    const [turnCards, setTurnCards] = useState([]); // Índices de cartas del turno actual (max 2)

    // Modales y control de audio
    const [isAudioMutedState, setIsAudioMutedState] = useState(() => isAudioMuted());
    const [toastMessage, setToastMessage] = useState('');
    const [showKickedOutModal, setShowKickedOutModal] = useState(false);
    const [kickedOutMessage, setKickedOutMessage] = useState('');

    // =========================================================================
    // 4. REFERENCIAS MUTABLES PARA TIEMPOS, WAKELOCK Y AUTO-VOLTEO
    // =========================================================================
    const wakeLockRef = useRef(null);
    const unflipTimerRef = useRef(null);
    const previewTimerRef = useRef(null);
    const isAudioMutedRef = useRef(isAudioMutedState);
    const cardsRef = useRef(cards);
    const turnCardsRef = useRef(turnCards);
    const isBoardLockedRef = useRef(isBoardLocked);
    const selectedPlayerSideRef = useRef(selectedPlayerSide);
    const enginePhaseRef = useRef(enginePhase);
    const activeThemeRef = useRef(activeTheme);
    const lastRegisteredRef = useRef({ side: '', connId: '' });

    useEffect(() => { isAudioMutedRef.current = isAudioMutedState; }, [isAudioMutedState]);
    useEffect(() => { cardsRef.current = cards; }, [cards]);
    useEffect(() => { turnCardsRef.current = turnCards; }, [turnCards]);
    useEffect(() => { isBoardLockedRef.current = isBoardLocked; }, [isBoardLocked]);
    useEffect(() => { selectedPlayerSideRef.current = selectedPlayerSide; }, [selectedPlayerSide]);
    useEffect(() => { enginePhaseRef.current = enginePhase; }, [enginePhase]);
    useEffect(() => { activeThemeRef.current = activeTheme; }, [activeTheme]);

    /**
     * Muestra un mensaje flotante tipo Toast.
     * @param {string} msg - Texto a mostrar.
     * @param {number} [duration=3000] - Duración en ms.
     */
    const showToast = useCallback((msg, duration = 3000) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(''), duration);
    }, []);

    // =========================================================================
    // 5. GESTIÓN DEL SCREEN WAKE LOCK API
    // =========================================================================
    const requestWakeLock = useCallback(async () => {
        try {
            if (typeof navigator !== 'undefined' && 'wakeLock' in navigator && !wakeLockRef.current) {
                const lock = await navigator.wakeLock.request('screen');
                wakeLockRef.current = lock;
                lock.addEventListener('release', () => {
                    wakeLockRef.current = null;
                });
            }
        } catch (e) {
            // Safe ignore if permissions or browser restricts WakeLock
        }
    }, []);

    const releaseWakeLock = useCallback(async () => {
        try {
            if (wakeLockRef.current) {
                await wakeLockRef.current.release();
                wakeLockRef.current = null;
            }
        } catch (e) {
            wakeLockRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (isAuthorized && enginePhase !== 'finished') {
            requestWakeLock();
        } else if (enginePhase === 'finished') {
            releaseWakeLock();
        }

        const handleVisibilityChange = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible' && isAuthorized && enginePhase !== 'finished') {
                requestWakeLock();
            }
        };

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            };
        }
    }, [selectedPlayerSide, enginePhase, requestWakeLock, releaseWakeLock]);

    // =========================================================================
    // 6. TOGGLE DE SILENCIO DE AUDIO
    // =========================================================================
    const handleToggleAudio = useCallback(() => {
        const nextMuted = toggleAudioMute();
        setIsAudioMutedState(nextMuted);
        if (!nextMuted) {
            unlockAudioContext();
        }
    }, []);

    // =========================================================================
    // 7. CARGA DE CONFIGURACIÓN Y PRECARGA EN CACHÉ
    // =========================================================================
    useEffect(() => {
        let isMounted = true;
        const fetchInitialConfig = async () => {
            if (!activeRoomId) return;
            try {
                const res = await GetConfig(activeRoomId);
                if (isMounted && res.success && res.data) {
                    if (res.data.activeTheme) {
                        setActiveTheme((prev) => ({ ...DEFAULT_THEME, ...(prev || {}), ...res.data.activeTheme }));
                        preloadImages(res.data.activeTheme);
                    }
                }
            } catch (err) {
                // Non-blocking fallback
            }
        };

        fetchInitialConfig();
        return () => { isMounted = false; };
    }, [activeRoomId]);


    // Sincronización continua de props
    useEffect(() => {
        if (propConfig && typeof propConfig === 'object') {
            setActiveTheme((prev) => ({ ...DEFAULT_THEME, ...(prev || {}), ...propConfig }));
            preloadImages(propConfig);
        }
    }, [propConfig]);

    useEffect(() => {
        if (propMatchState) {
            const rawP = propMatchState.phase || propMatchState.state;
            if (rawP) {
                const p = String(rawP).toLowerCase();
                setEnginePhase(p === 'playing' || p === 'running' ? 'playing' : p === 'countdown' ? 'countdown' : p === 'preview' ? 'preview' : p === 'finished' ? 'finished' : p === 'game_ready' ? 'game_ready' : p === 'cover' ? 'cover' : 'idle');
            }
            if (propMatchState.theme && typeof propMatchState.theme === 'object') {
                setActiveTheme((prev) => ({ ...DEFAULT_THEME, ...(prev || {}), ...propMatchState.theme }));
                preloadImages(propMatchState.theme);
            }
            if (propMatchState.winner !== undefined && propMatchState.winner !== null) {
                setMatchWinner(propMatchState.winner);
            }
        }
    }, [propMatchState]);

    useEffect(() => {
        if (propWinner !== undefined && propWinner !== null) {
            setMatchWinner(propWinner);
            setEnginePhase('finished');
        }
    }, [propWinner]);

    // =========================================================================
    // 8. MANEJO DEL PATRÓN KICKED_OUT (DESALOJO)
    // =========================================================================
    const handleKickedOut = useCallback((payload) => {
        console.warn('[MemoryPlayer] Desalojo kicked_out recibido:', payload);
        clearStoredFanPlayer(activeRoomId);
        clearMemoryAuth(activeRoomId);
        setRoomAccessPin('');
        setPinInput('');
        setPinError('');
        setIsAuthorized(false);
        releaseWakeLock();
        lastRegisteredRef.current = { side: '', connId: '' };

        setSelectedPlayerSide('');
        setEnginePhase('idle');
        setTurnCards([]);
        setIsBoardLocked(false);
        setMyScore(0);
        setMyTotalFlips(0);
        setRivalScore(0);
        setRivalTotalFlips(0);

        const msg = payload?.message || 'Tu personaje ha sido asignado a otro participante en esta sala.';
        setKickedOutMessage(msg);
        setShowKickedOutModal(true);
    }, [activeRoomId, releaseWakeLock]);

    // =========================================================================
    // 9. SINCRONIZACIÓN SIGNALR DE EVENTOS MEMORAMA EN TIEMPO REAL
    // =========================================================================
    const handleIncomingWebSocketMessage = useCallback((envelope) => {
        if (!envelope || !envelope.type) return;

        const rawType = String(envelope.type || '').toLowerCase();
        const data = envelope.payload !== undefined ? envelope.payload : envelope.data || {};
        const mySide = selectedPlayerSideRef.current;

        const incomingPin = data.accessPin || data.AccessPin || envelope.accessPin || envelope.AccessPin;
        if (incomingPin) {
            setRoomAccessPin(String(incomingPin));
        }
        const incomingRoom = envelope.roomId || envelope.RoomId || data.roomId || data.RoomId;
        if (incomingRoom && !activeRoomIdRef.current) {
            setActiveRoomId(incomingRoom);
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('active_room_id', incomingRoom);
            }
        }

        switch (rawType) {
            case 'kicked_out':
                handleKickedOut(data);
                break;

            case 'memory_countdown_started':
            case 'memory_countdown': {
                if (previewTimerRef.current) {
                    clearInterval(previewTimerRef.current);
                    previewTimerRef.current = null;
                }
                if (unflipTimerRef.current) {
                    clearTimeout(unflipTimerRef.current);
                    unflipTimerRef.current = null;
                }
                setEnginePhase('countdown');
                const count = data.countdownSeconds ?? data.step ?? data.count ?? 3;
                setCountdownValue(count);
                setMatchWinner(null);
                setWinningReason('');
                setMyScore(0);
                setMyTotalFlips(0);
                setRivalScore(0);
                setRivalTotalFlips(0);
                setTurnCards([]);
                setIsBoardLocked(false);

                const incomingPairs = data.pairsCount ?? data.PairsCount ?? data.matchState?.pairsCount ?? data.matchState?.PairsCount;
                if (incomingPairs && Number(incomingPairs) > 0 && Number(incomingPairs) !== activeThemeRef.current?.pairsCount) {
                    setActiveTheme((prev) => ({ ...prev, pairsCount: Number(incomingPairs) }));
                    setCards(createInitialCards(Number(incomingPairs) * 2));
                } else {
                    setCards((prev) => prev.map((c) => ({ ...c, isFlipped: false, isMatched: false })));
                }

                if (!isAudioMutedRef.current) {
                    playCountdownBeep(count === 1);
                }
                break;
            }

            case 'memory_preview':
            case 'memory_preview_start': {
                setEnginePhase('preview');
                const previewSec = Number(data.previewTimeSeconds ?? envelope.previewTimeSeconds ?? activeThemeRef.current?.previewTimeSeconds ?? 3);
                setPreviewRemainingSeconds(previewSec);
                setIsBoardLocked(true);

                const incomingPairs = data.pairsCount ?? data.PairsCount ?? data.matchState?.pairsCount ?? envelope.pairsCount;
                if (incomingPairs && Number(incomingPairs) > 0 && Number(incomingPairs) !== activeThemeRef.current?.pairsCount) {
                    setActiveTheme((prev) => ({ ...prev, pairsCount: Number(incomingPairs) }));
                }

                // Iniciar contador regresivo activo de segundos restantes en preview
                if (previewTimerRef.current) {
                    clearInterval(previewTimerRef.current);
                }
                let currentPreview = previewSec;
                previewTimerRef.current = setInterval(() => {
                    currentPreview -= 1;
                    if (currentPreview >= 0) {
                        setPreviewRemainingSeconds(currentPreview);
                    }
                    if (currentPreview <= 0) {
                        clearInterval(previewTimerRef.current);
                        previewTimerRef.current = null;
                    }
                }, 1000);

                // Extraer el tablero con máxima defensividad (soporta camelCase, PascalCase, payload y anidados)
                const rawSide = String(selectedPlayerSideRef.current || selectedPlayerSide || '').toLowerCase();
                const isP2 = rawSide === 'player2' || rawSide === 'j2' || rawSide === 'p2' || rawSide === '2';

                const mState = data.matchState || data.MatchState || envelope.matchState || envelope.MatchState || data.state || data.payload?.matchState || data.data?.matchState || (data.player1 ? data : null) || (envelope.player1 ? envelope : null) || data;
                const p1Data = mState?.player1 || mState?.Player1 || data.player1 || data.Player1 || envelope.player1;
                const p2Data = mState?.player2 || mState?.Player2 || data.player2 || data.Player2 || envelope.player2;
                const myTargetPlayer = isP2 ? p2Data : p1Data;

                const previewBoard = myTargetPlayer?.board || myTargetPlayer?.Board || mState?.board || mState?.Board || data.board;

                if (Array.isArray(previewBoard) && previewBoard.length > 0) {
                    setCards((prevCards) => previewBoard.map((c, i) => ({
                        cardIndex: c.cardIndex ?? c.CardIndex ?? i,
                        pairId: c.pairId ?? c.PairId ?? prevCards[i]?.pairId ?? null,
                        imageUrl: (c.imageUrl || c.ImageUrl) ? getResolvedMediaUrl(c.imageUrl || c.ImageUrl) : (prevCards[i]?.imageUrl || null),
                        isFlipped: true,
                        isMatched: false
                    })));
                } else {
                    // Voltear todas las cartas locales a 180deg manteniendo imágenes en caché si existen
                    setCards((prev) => prev.map((c) => ({ ...c, isFlipped: true })));
                }
                break;
            }

            case 'memory_preview_ended':
                if (previewTimerRef.current) {
                    clearInterval(previewTimerRef.current);
                    previewTimerRef.current = null;
                }
                setEnginePhase('playing');
                setIsBoardLocked(false);
                setTurnCards([]);
                // Voltear todas las cartas no emparejadas boca abajo (0deg)
                setCards((prev) => prev.map((c) => ({
                    ...c,
                    isFlipped: Boolean(c.isMatched)
                })));
                break;

            case 'memory_playing_started':
            case 'memory_start':
            case 'memory_game_start':
                if (previewTimerRef.current) {
                    clearInterval(previewTimerRef.current);
                    previewTimerRef.current = null;
                }
                setEnginePhase('playing');
                setIsBoardLocked(false);
                setTurnCards([]);
                setMyScore(0);
                setMyTotalFlips(0);
                setRivalScore(0);
                setRivalTotalFlips(0);
                setMatchWinner(null);
                setCards((prev) => prev.map((c) => ({ ...c, isFlipped: false, isMatched: false })));
                requestWakeLock();
                break;

            case 'memory_card_flipped': {
                const flippedSide = data.playerSide || data.side;
                const isMe = String(flippedSide).toLowerCase() === mySide;
                const cardIdx = Number(data.cardIndex);
                const pairId = data.pairId;
                const imageUrl = data.imageUrl ? getResolvedMediaUrl(data.imageUrl) : null;

                if (isMe) {
                    setCards((prev) => {
                        const next = [...prev];
                        if (next[cardIdx]) {
                            next[cardIdx] = {
                                ...next[cardIdx],
                                isFlipped: true,
                                pairId: pairId ?? next[cardIdx].pairId,
                                imageUrl: imageUrl ?? next[cardIdx].imageUrl
                            };
                        }
                        return next;
                    });
                    if (data.totalFlips !== undefined) {
                        setMyTotalFlips(data.totalFlips);
                    }
                } else {
                    if (data.totalFlips !== undefined) {
                        setRivalTotalFlips(data.totalFlips);
                    }
                }
                break;
            }

            case 'memory_match_success':
            case 'match_success': {
                const eventSide = data.playerSide || data.side;
                const isMe = String(eventSide).toLowerCase() === mySide;
                const c1 = Number(data.card1Index);
                const c2 = Number(data.card2Index);

                if (isMe) {
                    if (unflipTimerRef.current) {
                        clearTimeout(unflipTimerRef.current);
                        unflipTimerRef.current = null;
                    }

                    setCards((prev) => {
                        const next = [...prev];
                        if (next[c1]) next[c1] = { ...next[c1], isMatched: true, isFlipped: true };
                        if (next[c2]) next[c2] = { ...next[c2], isMatched: true, isFlipped: true };
                        return next;
                    });

                    setMyScore(data.score !== undefined ? data.score : (prev) => prev + 1);
                    if (data.totalFlips !== undefined) setMyTotalFlips(data.totalFlips);

                    setTurnCards([]);
                    setIsBoardLocked(false);

                    if (!isAudioMutedRef.current) playMatchSuccessSound();
                    hapticMatchSuccess();
                } else {
                    setRivalScore(data.score !== undefined ? data.score : (prev) => prev + 1);
                    if (data.totalFlips !== undefined) setRivalTotalFlips(data.totalFlips);
                }
                break;
            }

            case 'memory_match_failed':
            case 'match_failed': {
                const eventSide = data.playerSide || data.side;
                const isMe = String(eventSide).toLowerCase() === mySide;
                const c1 = Number(data.card1Index);
                const c2 = Number(data.card2Index);

                if (isMe) {
                    if (data.totalFlips !== undefined) setMyTotalFlips(data.totalFlips);
                    if (!isAudioMutedRef.current) playMatchFailedSound();
                    hapticMatchFailed();

                    // Fallback local por si el evento unflipped tarda
                    const delayMs = activeThemeRef.current?.hideUnmatchedDelayMs || 800;
                    if (unflipTimerRef.current) clearTimeout(unflipTimerRef.current);
                    unflipTimerRef.current = setTimeout(() => {
                        setCards((prev) => {
                            const next = [...prev];
                            if (next[c1] && !next[c1].isMatched) next[c1] = { ...next[c1], isFlipped: false };
                            if (next[c2] && !next[c2].isMatched) next[c2] = { ...next[c2], isFlipped: false };
                            return next;
                        });
                        setTurnCards([]);
                        setIsBoardLocked(false);
                    }, delayMs);
                } else {
                    if (data.totalFlips !== undefined) setRivalTotalFlips(data.totalFlips);
                }
                break;
            }

            case 'memory_cards_unflipped':
            case 'cards_unflipped': {
                const eventSide = data.playerSide || data.side;
                const isMe = String(eventSide).toLowerCase() === mySide;
                const c1 = Number(data.card1Index);
                const c2 = Number(data.card2Index);

                if (isMe) {
                    if (unflipTimerRef.current) {
                        clearTimeout(unflipTimerRef.current);
                        unflipTimerRef.current = null;
                    }
                    setCards((prev) => {
                        const next = [...prev];
                        if (next[c1] && !next[c1].isMatched) next[c1] = { ...next[c1], isFlipped: false };
                        if (next[c2] && !next[c2].isMatched) next[c2] = { ...next[c2], isFlipped: false };
                        return next;
                    });
                    setTurnCards([]);
                    setIsBoardLocked(false);
                }
                break;
            }

            case 'memory_telemetry': {
                if (data.remainingSeconds !== undefined) {
                    setRemainingMatchSeconds(Math.max(0, Math.round(data.remainingSeconds)));
                }
                if (data.player1 && data.player2) {
                    const myData = mySide === 'player2' ? data.player2 : data.player1;
                    const rivalData = mySide === 'player2' ? data.player1 : data.player2;
                    if (myData) {
                        setMyScore(myData.score);
                        setMyTotalFlips(myData.totalFlips);
                    }
                    if (rivalData) {
                        setRivalScore(rivalData.score);
                        setRivalTotalFlips(rivalData.totalFlips);
                    }
                }
                break;
            }

            case 'memory_all_pairs_matched':
            case 'all_pairs_matched':
            case 'memory_finished':
            case 'memory_game_over':
            case 'memory_game_finished': {
                setEnginePhase('finished');
                releaseWakeLock();

                const winnerRaw = data.winner || data.winnerSide || 'tie';
                const reason = data.winningReason || data.reason || 'all_pairs_matched';
                setMatchWinner(winnerRaw);
                setWinningReason(reason);

                const isMeWinner = String(winnerRaw).toLowerCase() === mySide;
                if (isMeWinner) {
                    if (!isAudioMutedRef.current) playVictoryFanfare();
                    hapticVictory();
                }
                break;
            }

            case 'memory_cover_shown':
            case 'memory_cover': {
                setEnginePhase('cover');
                const incomingPairs = data.pairsCount ?? data.PairsCount ?? data.matchState?.pairsCount ?? data.matchState?.PairsCount ?? data.theme?.pairsCount;
                if (data.theme) {
                    setActiveTheme((prev) => ({ ...prev, ...data.theme, ...(incomingPairs ? { pairsCount: Number(incomingPairs) } : {}) }));
                } else if (incomingPairs && Number(incomingPairs) > 0) {
                    setActiveTheme((prev) => ({ ...prev, pairsCount: Number(incomingPairs) }));
                }
                setMatchWinner(null);
                break;
            }

            case 'memory_sync_state':
            case 'memory_state':
            case 'memory_game_ready':
            case 'memory_game_shown': {
                if (previewTimerRef.current) {
                    clearInterval(previewTimerRef.current);
                    previewTimerRef.current = null;
                }
                if (unflipTimerRef.current) {
                    clearTimeout(unflipTimerRef.current);
                    unflipTimerRef.current = null;
                }
                setEnginePhase('game_ready');
                const incomingPairs = data.pairsCount ?? data.PairsCount ?? data.matchState?.pairsCount ?? data.matchState?.PairsCount ?? data.theme?.pairsCount;
                const newPairs = incomingPairs && Number(incomingPairs) > 0 ? Number(incomingPairs) : (activeThemeRef.current?.pairsCount || 6);

                if (data.theme) {
                    setActiveTheme((prev) => ({ ...prev, ...data.theme, pairsCount: newPairs }));
                } else if (incomingPairs && Number(incomingPairs) > 0) {
                    setActiveTheme((prev) => ({ ...prev, pairsCount: newPairs }));
                }

                setMatchWinner(null);
                setWinningReason('');
                setTurnCards([]);
                setIsBoardLocked(false);
                setMyScore(0);
                setMyTotalFlips(0);
                setRivalScore(0);
                setRivalTotalFlips(0);

                // Sincronizar longitud y contenido de tablero
                const rawSide = String(selectedPlayerSideRef.current || selectedPlayerSide || '').toLowerCase();
                const isP2 = rawSide === 'player2' || rawSide === 'j2' || rawSide === 'p2' || rawSide === '2';
                const mState = data.matchState || data.MatchState || data.state || data.payload?.matchState || data.data?.matchState || data;
                const p1Data = mState?.player1 || mState?.Player1 || data.player1 || data.Player1;
                const p2Data = mState?.player2 || mState?.Player2 || data.player2 || data.Player2;
                const myTargetPlayer = isP2 ? p2Data : p1Data;
                const incomingBoard = myTargetPlayer?.board || myTargetPlayer?.Board || mState?.board || mState?.Board || data.board;

                if (Array.isArray(incomingBoard) && incomingBoard.length > 0) {
                    setCards(incomingBoard.map((c, i) => ({
                        cardIndex: c.cardIndex ?? c.CardIndex ?? i,
                        pairId: c.pairId ?? c.PairId ?? null,
                        imageUrl: getResolvedMediaUrl(c.imageUrl || c.ImageUrl),
                        isFlipped: Boolean(c.isFlipped),
                        isMatched: Boolean(c.isMatched)
                    })));
                } else {
                    setCards(createInitialCards(newPairs * 2));
                }
                break;
            }

            case 'memory_match_reset':
            case 'memory_game_reset':
            case 'memory_reset': {
                if (previewTimerRef.current) {
                    clearInterval(previewTimerRef.current);
                    previewTimerRef.current = null;
                }
                if (unflipTimerRef.current) {
                    clearTimeout(unflipTimerRef.current);
                    unflipTimerRef.current = null;
                }
                setEnginePhase('game_ready');
                setMatchWinner(null);
                setWinningReason('');
                setTurnCards([]);
                setIsBoardLocked(false);
                setMyScore(0);
                setMyTotalFlips(0);
                setRivalScore(0);
                setRivalTotalFlips(0);
                const currentPairs = activeThemeRef.current?.pairsCount || 6;
                setCards(createInitialCards(currentPairs * 2));
                break;
            }

            default:
                break;
        }
    }, [handleKickedOut, releaseWakeLock]);

    const incomingHandlerRef = useRef(handleIncomingWebSocketMessage);
    const kickedOutHandlerRef = useRef(handleKickedOut);
    const connectionStatusHandlerRef = useRef(setConnectionStatus);

    useEffect(() => { incomingHandlerRef.current = handleIncomingWebSocketMessage; }, [handleIncomingWebSocketMessage]);
    useEffect(() => { kickedOutHandlerRef.current = handleKickedOut; }, [handleKickedOut]);
    useEffect(() => { connectionStatusHandlerRef.current = setConnectionStatus; }, []);

    // WebSocket lifecycle effect (conexión persistente libre de loops)
    useEffect(() => {
        if (!selectedPlayerSide) {
            DisconnectWebSocketForMemoryFan();
            return;
        }

        ConnectWebSocketForMemoryFan(
            (envelope) => incomingHandlerRef.current?.(envelope),
            (status) => connectionStatusHandlerRef.current?.(status),
            activeRoomId || '',
            selectedPlayerSide,
            {
                onKickedOut: (d) => kickedOutHandlerRef.current?.(d),
                onCountdown: (d) => incomingHandlerRef.current?.({ type: 'memory_countdown', payload: d }),
                onPreview: (d) => incomingHandlerRef.current?.({ type: 'memory_preview', payload: d }),
                onPreviewEnded: (d) => incomingHandlerRef.current?.({ type: 'memory_preview_ended', payload: d }),
                onGameStart: (d) => incomingHandlerRef.current?.({ type: 'memory_playing_started', payload: d }),
                onCardFlipped: (d) => incomingHandlerRef.current?.({ type: 'memory_card_flipped', payload: d }),
                onMatchSuccess: (d) => incomingHandlerRef.current?.({ type: 'memory_match_success', payload: d }),
                onMatchFailed: (d) => incomingHandlerRef.current?.({ type: 'memory_match_failed', payload: d }),
                onCardsUnflipped: (d) => incomingHandlerRef.current?.({ type: 'memory_cards_unflipped', payload: d }),
                onTelemetry: (d) => incomingHandlerRef.current?.({ type: 'memory_telemetry', payload: d }),
                onFinished: (d) => incomingHandlerRef.current?.({ type: 'memory_finished', payload: d }),
                onGameOver: (d) => incomingHandlerRef.current?.({ type: 'memory_finished', payload: d })
            }
        );

        return () => {
            DisconnectWebSocketForMemoryFan();
            releaseWakeLock();
            if (unflipTimerRef.current) clearTimeout(unflipTimerRef.current);
            if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
        };
    }, [selectedPlayerSide, activeRoomId, releaseWakeLock]);

    // Sincronización única de ConnectionId tras conectarse
    useEffect(() => {
        const currentRoom = activeRoomIdRef.current || activeRoomId;
        if (connectionStatus === 'connected' && selectedPlayerSide && currentRoom) {
            const connId = SignalRService.getConnectionId();
            if (connId && (lastRegisteredRef.current.side !== selectedPlayerSide || lastRegisteredRef.current.connId !== connId)) {
                lastRegisteredRef.current = { side: selectedPlayerSide, connId };
                const currentName = selectedPlayerSide === 'player1'
                    ? (activeThemeRef.current?.player1Name || 'Jugador 1')
                    : (activeThemeRef.current?.player2Name || 'Jugador 2');
                const currentAuth = getMemoryAuth(currentRoom);
                const authPin = currentAuth?.pin || roomAccessPin || decodedToken?.pin || '';
                SelectPlayerSide(currentRoom, selectedPlayerSide, currentName, connId, authPin).then((res) => {
                    if (res && !res.success && (res.message?.includes('PIN') || res.message?.includes('pin'))) {
                        handleKickedOut({ message: res.message });
                    }
                }).catch((err) => {
                    console.warn('[MemoryPlayer] Error en SelectPlayerSide tras conexión:', err);
                });
            }
        }
    }, [connectionStatus, selectedPlayerSide, activeRoomId]);

    // =========================================================================
    // 10. ACCIONES: SELECCIÓN DE PERSONAJE Y SALIDA
    // =========================================================================
    const handleSelectSide = async (sideChosen) => {
        unlockAudioContext();
        await requestWakeLock();

        const defaultName = sideChosen === 'player1'
            ? (activeThemeRef.current?.player1Name || 'Jugador 1')
            : (activeThemeRef.current?.player2Name || 'Jugador 2');
        setSelectedPlayerSide(sideChosen);
        const currentRoom = activeRoomIdRef.current || activeRoomId || '';
        if (currentRoom) {
            setStoredFanPlayer(currentRoom, sideChosen, defaultName);
        }

        setEnginePhase('game_ready');
        setMyScore(0);
        setMyTotalFlips(0);
        setRivalScore(0);
        setRivalTotalFlips(0);
        setTurnCards([]);
        setIsBoardLocked(false);
        setCards(createInitialCards(totalCardsCount));

        try {
            const connId = SignalRService.getConnectionId() || '';
            if (connId) {
                lastRegisteredRef.current = { side: sideChosen, connId };
            }
            await SelectPlayerSide(currentRoom, sideChosen, defaultName, connId);
            showToast(`¡Bando ${sideChosen === 'player1' ? 'Jugador 1' : 'Jugador 2'} seleccionado!`);
        } catch (err) {
            console.warn('[MemoryPlayer] Error al registrar selección de bando:', err);
        }
    };

    const handleLeaveGame = async () => {
        const currentRoom = activeRoomIdRef.current || activeRoomId || '';
        try {
            const connId = SignalRService.getConnectionId() || '';
            await LeaveGame(currentRoom, connId, selectedPlayerSide);
        } catch (err) {
            console.warn('[MemoryPlayer] Error al salir de la partida:', err);
        }

        if (currentRoom) {
            clearStoredFanPlayer(currentRoom);
            clearMemoryAuth(currentRoom);
        }
        setIsAuthorized(false);
        DisconnectWebSocketForMemoryFan();
        releaseWakeLock();
        lastRegisteredRef.current = { side: '', connId: '' };

        setSelectedPlayerSide('');
        setEnginePhase('idle');
        setTurnCards([]);
        setIsBoardLocked(false);
        setMyScore(0);
        setMyTotalFlips(0);
        setRivalScore(0);
        setRivalTotalFlips(0);
        setCards(createInitialCards(totalCardsCount));
    };

    // =========================================================================
    // 11. MANEJADOR TÁCTIL DE CARTAS CON OPTIMISTIC UI (0ms)
    // =========================================================================
    const handleCardPointerDown = useCallback((cardIndex) => {
        if (enginePhaseRef.current !== 'playing') return;
        if (isBoardLockedRef.current) return;

        const currentCards = cardsRef.current;
        const targetCard = currentCards[cardIndex];
        if (!targetCard) return;
        if (targetCard.isFlipped || targetCard.isMatched) return;

        const currentTurns = turnCardsRef.current;
        if (currentTurns.includes(cardIndex)) return;

        // 1. Desbloquear Web Audio si estaba suspendido
        unlockAudioContext();

        // 2. Feedback acústico y háptico ultra-rápido (0ms)
        if (!isAudioMutedRef.current) {
            playFlipSound();
        }
        hapticFlip();

        // 3. Optimistic UI: Voltear de inmediato la carta localmente (0ms)
        setCards((prev) => {
            const next = [...prev];
            next[cardIndex] = { ...next[cardIndex], isFlipped: true };
            return next;
        });

        const newTurnCards = [...currentTurns, cardIndex];
        setTurnCards(newTurnCards);

        // Si es la 2da carta: Bloqueo preventivo inmediato del tablero
        if (newTurnCards.length >= 2) {
            setIsBoardLocked(true);
            setMyTotalFlips((prev) => prev + 1);
        }

        // 4. Enviar acción autoritativa al backend
        const connId = SignalRService.getConnectionId() || null;
        const currentRoom = activeRoomIdRef.current || activeRoomId || '';
        FlipCard(currentRoom, selectedPlayerSideRef.current, cardIndex, connId)
            .then((res) => {
                if (res && res.success && res.data) {
                    const flipData = res.data;
                    setCards((prev) => {
                        const next = [...prev];
                        if (next[cardIndex]) {
                            next[cardIndex] = {
                                ...next[cardIndex],
                                isFlipped: true,
                                pairId: flipData.pairId ?? next[cardIndex].pairId,
                                imageUrl: flipData.imageUrl ? getResolvedMediaUrl(flipData.imageUrl) : next[cardIndex].imageUrl
                            };
                        }
                        return next;
                    });
                }
            })
            .catch((err) => {
                console.error('[MemoryPlayer] Error al voltear carta en servidor:', err);
            });
    }, [activeRoomId]);

    // =========================================================================
    // 12. HELPERS VISUALES DE BANDO Y FONDOS
    // =========================================================================
    const isP1 = selectedPlayerSide === 'player1';
    const myTeamColor = isP1
        ? (activeTheme?.player1Color || DEFAULT_THEME.player1Color)
        : (activeTheme?.player2Color || DEFAULT_THEME.player2Color);
    const myTeamName = isP1
        ? (activeTheme?.player1Name || DEFAULT_THEME.player1Name)
        : (activeTheme?.player2Name || DEFAULT_THEME.player2Name);
    const myTeamAvatar = isP1
        ? getResolvedMediaUrl(activeTheme?.player1Avatar)
        : getResolvedMediaUrl(activeTheme?.player2Avatar);
    const myWinnerAvatar = isP1
        ? getResolvedMediaUrl(activeTheme?.player1Winner)
        : getResolvedMediaUrl(activeTheme?.player2Winner);

    const renderDynamicBackground = () => {
        const bgType = activeTheme?.fanBackgroundType || 'color';
        const bgColor = activeTheme?.fanBackgroundColor || DEFAULT_THEME.fanBackgroundColor;
        const bgUrl = activeTheme?.fanBackgroundUrl ? getResolvedMediaUrl(activeTheme.fanBackgroundUrl) : null;

        if (bgType === 'image' && bgUrl) {
            return (
                <div className="memory-fan__bg">
                    <img src={bgUrl} alt="Fondo" className="memory-fan__bg-image" />
                    <div className="memory-fan__bg-overlay" />
                </div>
            );
        }
        if (bgType === 'video' && bgUrl) {
            return (
                <div className="memory-fan__bg">
                    <video src={bgUrl} autoPlay loop muted playsInline className="memory-fan__bg-video" />
                    <div className="memory-fan__bg-overlay" />
                </div>
            );
        }
        return (
            <div className="memory-fan__bg" style={{ backgroundColor: bgColor }}>
                <div className="memory-fan__bg-overlay" />
            </div>
        );
    };

    const handleVerifyPin = async (e) => {
        if (e) e.preventDefault();
        if (pinCooldownSeconds > 0) return;
        const cleanEntered = pinInput.trim();
        if (cleanEntered.length !== 4) {
            setPinError('Ingresa los 4 dígitos del PIN.');
            return;
        }

        const currentRoom = activeRoomIdRef.current || activeRoomId;
        let validPin = null;

        // Siempre consultar al backend el PIN oficial y vigente en tiempo real
        if (currentRoom) {
            try {
                const res = await GetMatchState(currentRoom);
                if (res && res.success && res.data) {
                    const serverPin = res.data.accessPin || res.data.AccessPin;
                    if (serverPin) {
                        validPin = String(serverPin).trim();
                        setRoomAccessPin(validPin);
                    }
                }
            } catch (err) {
                console.warn('[MemoryPlayer] Error al consultar PIN oficial:', err);
            }
        }

        // Si la red falló, fallback al PIN en memoria si existía
        if (!validPin && roomAccessPin) {
            validPin = String(roomAccessPin).trim();
        }

        if (validPin && cleanEntered === String(validPin).trim()) {
            setPinError('');
            setPinFailedAttempts(0);
            setMemoryAuth(currentRoom, cleanEntered);
            setIsAuthorized(true);
            requestWakeLock();
            showToast('¡PIN correcto! Bienvenido a Memorama.');
        } else {
            const nextAttempts = pinFailedAttempts + 1;
            setPinFailedAttempts(nextAttempts);
            setPinInput('');
            if (nextAttempts >= 3) {
                setPinCooldownSeconds(30);
                setPinError('Demasiados intentos fallidos. Por favor espera 30 segundos.');
            } else {
                setPinError(`PIN incorrecto (intento ${nextAttempts} de 3). Verifica con el staff.`);
            }
        }
    };

    // =========================================================================
    // VISTA 0: MODAL DE AUTENTICACIÓN POR PIN (SI NO HAY TOKEN NI SESIÓN)
    // =========================================================================
    if (!isAuthorized) {
        return (
            <GoalBase classNameApplied={'Fan__Goal__Header'}>
                <div className="memory-fan__selection d-flex flex-column align-items-center justify-content-center px-4" style={{ minHeight: '75vh' }}>
                    {showKickedOutModal && (
                        <div className="memory-fan__modal-overlay">
                            <div className="memory-fan__modal-card">
                                <i className="bi bi-exclamation-triangle-fill text-warning fs-1 mb-2"></i>
                                <h4 className="fw-bold text-white mb-2">Sesión Finalizada</h4>
                                <p className="text-white-50 small mb-3">{kickedOutMessage}</p>
                                <button
                                    type="button"
                                    className="Btn__Adsl--Red w-100 py-2"
                                    onClick={() => setShowKickedOutModal(false)}
                                >
                                    Entendido
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="bg-dark bg-opacity-75 p-4 rounded-4 text-center border border-secondary shadow-lg w-100" style={{ maxWidth: '360px' }}>
                        <div className="mb-3">
                            <i className="bi bi-shield-lock-fill text-warning" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h2 className="text-white fw-bold mb-2">Acceso a Memorama</h2>
                        <p className="text-white-50 small mb-4">
                            Ingresa el PIN de 4 dígitos proporcionado por el staff o escanea el código QR oficial.
                        </p>

                        <form onSubmit={handleVerifyPin}>
                            <div className="mb-3">
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={4}
                                    placeholder="----"
                                    value={pinInput}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                                        setPinInput(val);
                                        if (pinError) setPinError('');
                                    }}
                                    disabled={pinCooldownSeconds > 0}
                                    className="form-control form-control-lg text-center fw-bold bg-black text-warning border-secondary"
                                    style={{
                                        fontSize: '2rem',
                                        letterSpacing: '12px',
                                        borderRadius: '12px'
                                    }}
                                    autoFocus
                                />
                            </div>

                            {pinError && (
                                <div className="alert alert-danger py-2 px-3 small mb-3">
                                    <i className="bi bi-exclamation-circle-fill me-1"></i>
                                    {pinError}
                                </div>
                            )}

                            {pinCooldownSeconds > 0 && (
                                <div className="alert alert-warning py-2 px-3 small mb-3">
                                    <i className="bi bi-clock-history me-1"></i>
                                    Pausa de seguridad: espera <strong>{pinCooldownSeconds}s</strong>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={pinInput.length !== 4 || pinCooldownSeconds > 0}
                                className="Btn__Adsl--Red w-100 py-3 fw-bold fs-6"
                            >
                                <i className="bi bi-box-arrow-in-right me-1"></i> Ingresar a la Partida
                            </button>
                        </form>
                    </div>
                </div>
            </GoalBase>
        );
    }

    // =========================================================================
    // 13. VISTA 1: SELECCIÓN DE PERSONAJE (SOBRE GOALBASE)
    // =========================================================================
    if (!selectedPlayerSide) {
        return (
            <GoalBase classNameApplied={'Fan__Goal__Header'}>
                <div className="memory-fan__selection">
                    {/* Modal kicked_out si fue expulsado previamente */}
                    {showKickedOutModal && (
                        <div className="memory-fan__modal-overlay">
                            <div className="memory-fan__modal-card">
                                <i className="bi bi-exclamation-triangle-fill text-warning fs-1 mb-2"></i>
                                <h4 className="fw-bold text-white mb-2">Sesión Reasignada</h4>
                                <p className="text-white-50 small mb-3">{kickedOutMessage}</p>
                                <button
                                    type="button"
                                    className="Btn__Adsl--Red w-100 py-2"
                                    onClick={() => setShowKickedOutModal(false)}
                                >
                                    Entendido
                                </button>
                            </div>
                        </div>
                    )}

                    <h2 className="memory-fan__hero-title">¡Elige a tu Personaje!</h2>
                    <p className="memory-fan__hero-subtitle">Memorama 1 vs 1 · Encuentra más pares que tu rival</p>

                    <div className="memory-fan__cards-duo">
                        {/* Tarjeta Jugador 1 */}
                        <button
                            type="button"
                            className="memory-fan__team-btn memory-fan__team-btn--p1"
                            onClick={() => handleSelectSide('player1')}
                            style={{ borderColor: activeTheme?.player1Color || DEFAULT_THEME.player1Color }}
                        >
                            <div className="memory-fan__team-avatar-wrap">
                                {activeTheme?.player1Avatar ? (
                                    <img
                                        src={getResolvedMediaUrl(activeTheme.player1Avatar)}
                                        alt="Avatar J1"
                                        className="memory-fan__team-avatar"
                                    />
                                ) : (
                                    <div
                                        className="memory-fan__avatar-placeholder"
                                        style={{ backgroundColor: activeTheme?.player1Color || DEFAULT_THEME.player1Color }}
                                    >
                                        <i className="bi bi-person-fill fs-2 text-dark"></i>
                                    </div>
                                )}
                            </div>
                            <div className="memory-fan__team-info">
                                <h3 className="memory-fan__team-name">
                                    {activeTheme?.player1Name || 'Jugador 1'}
                                </h3>
                            </div>
                            <i className="bi bi-chevron-right memory-fan__team-arrow" style={{ color: activeTheme?.player1Color || DEFAULT_THEME.player1Color }}></i>
                        </button>

                        {/* Tarjeta Jugador 2 */}
                        <button
                            type="button"
                            className="memory-fan__team-btn memory-fan__team-btn--p2"
                            onClick={() => handleSelectSide('player2')}
                            style={{ borderColor: activeTheme?.player2Color || DEFAULT_THEME.player2Color }}
                        >
                            <div className="memory-fan__team-avatar-wrap">
                                {activeTheme?.player2Avatar ? (
                                    <img
                                        src={getResolvedMediaUrl(activeTheme.player2Avatar)}
                                        alt="Avatar J2"
                                        className="memory-fan__team-avatar"
                                    />
                                ) : (
                                    <div
                                        className="memory-fan__avatar-placeholder"
                                        style={{ backgroundColor: activeTheme?.player2Color || DEFAULT_THEME.player2Color }}
                                    >
                                        <i className="bi bi-person-fill fs-2 text-dark"></i>
                                    </div>
                                )}
                            </div>
                            <div className="memory-fan__team-info">
                                <h3 className="memory-fan__team-name">
                                    {activeTheme?.player2Name || 'Jugador 2'}
                                </h3>
                            </div>
                            <i className="bi bi-chevron-right memory-fan__team-arrow" style={{ color: activeTheme?.player2Color || DEFAULT_THEME.player2Color }}></i>
                        </button>
                    </div>
                </div>
            </GoalBase>
        );
    }

    // =========================================================================
    // 14. VISTAS ACTIVAS (SALA DE ESPERA, CONTEO, PREVIEW, JUEGO Y RESULTADOS)
    // =========================================================================
    return (
        <div className="memory-fan">
            {renderDynamicBackground()}

            {/* Toast flotante */}
            {toastMessage && (
                <div className="memory-fan__toast">
                    {toastMessage}
                </div>
            )}

            {/* Cabecera superior común */}
            <header className="memory-fan__header">
                <div className="memory-fan__header-brand">
                    {activeTheme?.brandLogoUrl ? (
                        <img
                            src={getResolvedMediaUrl(activeTheme.brandLogoUrl)}
                            alt="Logo"
                            className="memory-fan__header-logo"
                        />
                    ) : (
                        <img
                            src="/img/page/xolos-five.png"
                            alt="Logo"
                            className="memory-fan__header-logo"
                        />
                    )}
                </div>

                <div className="memory-fan__header-controls">
                    <span className={`memory-fan__status-badge ${connectionStatus}`}>
                        {connectionStatus === 'connected' ? 'En Línea' : 'Conectando'}
                    </span>

                    <button
                        type="button"
                        className={`memory-fan__icon-btn ${isAudioMutedState ? 'is-muted' : ''}`}
                        onClick={handleToggleAudio}
                        title={isAudioMutedState ? 'Activar Sonido' : 'Silenciar'}
                    >
                        <i className={`bi ${isAudioMutedState ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`}></i>
                    </button>

                    <button
                        type="button"
                        className="memory-fan__exit-btn"
                        onClick={handleLeaveGame}
                        title="Salir de la partida"
                    >
                        <i className="bi bi-box-arrow-left"></i> Salir
                    </button>
                </div>
            </header>

            {/* ============================================================= */}
            {/* SUB-VISTA A: SALA DE ESPERA (WAITING / GAME_READY / COVER)   */}
            {/* ============================================================= */}
            {(enginePhase === 'idle' || enginePhase === 'cover' || enginePhase === 'game_ready') && (
                <main className="memory-fan__waiting">
                    <div className="memory-fan__avatar-energy-box">
                        <div
                            className="memory-fan__energy-ring"
                            style={{ borderColor: myTeamColor }}
                        />
                        {myTeamAvatar ? (
                            <img src={myTeamAvatar} alt={myTeamName} className="memory-fan__waiting-avatar" />
                        ) : (
                            <div className="memory-fan__waiting-avatar-fallback" style={{ backgroundColor: myTeamColor }}>
                                <i className="bi bi-person-fill display-3 text-dark"></i>
                            </div>
                        )}
                    </div>

                    <div className="memory-fan__waiting-tag" style={{ color: myTeamColor }}>
                        ¡PREPÁRATE!
                    </div>

                    <div className="memory-fan__waiting-player-info">
                        <h2 className="memory-fan__waiting-name" style={{ color: myTeamColor }}>
                            {myTeamName}
                        </h2>
                        {/* <p className="memory-fan__waiting-fan-name">{isP1 ? 'LADO A' : 'LADO B'}</p> */}
                    </div>

                    <div className="memory-fan__waiting-instructions">
                        <p className="mb-1"><i className="bi bi-eye-fill text-warning me-1"></i> Memoriza las cartas en la fase previa.</p>
                        <p className="mb-0"><i className="bi bi-hand-index-thumb-fill text-info me-1"></i> Voltea 2 cartas por turno. ¡Encuentra todos los pares!</p>
                    </div>
                </main>
            )}

            {/* ============================================================= */}
            {/* SUB-VISTA B: CUENTA REGRESIVA ARCADE (3, 2, 1)              */}
            {/* ============================================================= */}
            {enginePhase === 'countdown' && typeof countdownValue === 'number' && countdownValue > 0 && (
                <main className="memory-fan__countdown-overlay">
                    <div className="memory-fan__countdown-title">¡PREPÁRATE!</div>
                    <div className="memory-fan__countdown-number" key={countdownValue}>
                        {countdownValue}
                    </div>
                    <p className="memory-fan__countdown-subtitle">Memoriza la posición de las figuras...</p>
                </main>
            )}

            {/* ============================================================= */}
            {/* SUB-VISTA C: FASE DE PREVIEW (MEMORIZACIÓN DESCUBIERTA)      */}
            {/* ============================================================= */}
            {enginePhase === 'preview' && (
                <main className="memory-fan__preview-container">
                    <div className="memory-fan__preview-banner">
                        <i className="bi bi-eye-fill me-1"></i> ¡MEMORIZA LAS CARTAS! ({previewRemainingSeconds}s)
                    </div>

                    <div className={`memory-fan__grid ${gridClass} memory-fan__grid--locked`}>
                        {cards.map((card, idx) => (
                            <div
                                key={`preview-card-${idx}`}
                                className="memory-card is-flipped memory-card--flipped"
                            >
                                <div className="memory-card__inner">
                                    {/* Cara Cubierta (Back / Boca Abajo) */}
                                    <div
                                        className="memory-card__face memory-card__face--back"
                                        style={{ backgroundColor: activeTheme?.cardBackBackgroundColor || '#1e293b' }}
                                    >
                                        {activeTheme?.cardBackLogoUrl ? (
                                            <img
                                                src={getResolvedMediaUrl(activeTheme.cardBackLogoUrl)}
                                                alt="Reverso"
                                                className="memory-card__back-logo"
                                                draggable="false"
                                            />
                                        ) : (
                                            <div className="memory-card__default-back">
                                                <i className="bi bi-question-diamond-fill fs-3" style={{ color: myTeamColor }}></i>
                                            </div>
                                        )}
                                    </div>

                                    {/* Cara Descubierta (Front / Boca Arriba) */}
                                    <div className="memory-card__face memory-card__face--front">
                                        {card.imageUrl ? (
                                            <img
                                                src={getResolvedMediaUrl(card.imageUrl)}
                                                alt={`Carta ${idx}`}
                                                className="memory-card__img"
                                                draggable="false"
                                            />
                                        ) : (
                                            <div className="memory-card__fallback-figure">
                                                <span className="memory-card__pair-text">{card.pairId !== null ? `PAR #${card.pairId}` : '★'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="memory-fan__footer-hint">
                        <span className="text-warning fw-bold">
                            <i className="bi bi-clock-history me-1"></i> La partida comenzará en breve...
                        </span>
                    </div>
                </main>
            )}

            {/* ============================================================= */}
            {/* SUB-VISTA D: TABLERO DINÁMICO TÁCTIL (PLAYING / OPTIMISTIC UI)*/}
            {/* ============================================================= */}
            {enginePhase === 'playing' && (
                <main className="memory-fan__playing-container">
                    {/* Barra de métricas y marcador superior */}
                    <div className="memory-fan__scoreboard">
                        <div className="memory-fan__score-box">
                            <span className="memory-fan__score-label">Pares</span>
                            <span className="memory-fan__score-value" style={{ color: myTeamColor }}>
                                {myScore} <small className="text-white-50 fs-6">/ {pairsCount}</small>
                            </span>
                        </div>

                        <div className="memory-fan__score-box">
                            <span className="memory-fan__score-label">Movimientos</span>
                            <span className="memory-fan__score-value text-white">
                                {myTotalFlips}
                            </span>
                        </div>

                        <div className="memory-fan__score-box">
                            <span className="memory-fan__score-label">Rival</span>
                            <span className="memory-fan__score-value text-white-50">
                                {rivalScore} <small className="text-white-50 fs-6">pts</small>
                            </span>
                        </div>
                    </div>

                    {/* Tablero táctil 4 columnas */}
                    <div className={`memory-fan__grid ${gridClass} ${isBoardLocked ? 'memory-fan__grid--locked' : ''}`}>
                        {cards.map((card, idx) => {
                            const isFlipped = card.isFlipped || card.isMatched;
                            const isMatched = card.isMatched;

                            return (
                                <div
                                    key={`card-${idx}`}
                                    className={`memory-card ${isFlipped ? 'is-flipped memory-card--flipped' : ''} ${isMatched ? 'is-matched memory-card--matched' : ''}`}
                                    onClick={() => handleCardPointerDown(idx)}
                                    style={{
                                        touchAction: 'manipulation'
                                    }}
                                >
                                    <div className="memory-card__inner">
                                        {/* Cara Cubierta (Back / Boca Abajo) */}
                                        <div
                                            className="memory-card__face memory-card__face--back"
                                            style={{ backgroundColor: activeTheme?.cardBackBackgroundColor || '#1e293b' }}
                                        >
                                            {activeTheme?.cardBackLogoUrl ? (
                                                <img
                                                    src={getResolvedMediaUrl(activeTheme.cardBackLogoUrl)}
                                                    alt="Reverso"
                                                    className="memory-card__back-logo"
                                                    draggable="false"
                                                />
                                            ) : (
                                                <div className="memory-card__default-back">
                                                    <i className="bi bi-question-diamond-fill fs-3" style={{ color: myTeamColor }}></i>
                                                </div>
                                            )}
                                        </div>

                                        {/* Cara Descubierta (Front / Boca Arriba) */}
                                        <div className="memory-card__face memory-card__face--front">
                                            {card.imageUrl ? (
                                                <img
                                                    src={getResolvedMediaUrl(card.imageUrl)}
                                                    alt={`Carta ${idx}`}
                                                    className="memory-card__img"
                                                    draggable="false"
                                                />
                                            ) : (
                                                <div className="memory-card__fallback-figure">
                                                    <span className="memory-card__pair-text">{card.pairId !== null ? `PAR #${card.pairId}` : '★'}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Indicador de estado inferior */}
                    <div className="memory-fan__footer-hint">
                        {isBoardLocked ? (
                            <span className="text-warning fw-bold">
                                <i className="bi bi-hourglass-split me-1"></i> Evaluando par...
                            </span>
                        ) : (
                            <span className="text-white-50">
                                <i className="bi bi-hand-index-thumb me-1"></i> Toca una carta para voltearla
                            </span>
                        )}
                    </div>
                </main>
            )}

            {/* ============================================================= */}
            {/* SUB-VISTA E: PANTALLA DE RESULTADOS (FINISHED)               */}
            {/* ============================================================= */}
            {enginePhase === 'finished' && (() => {
                const isMeWinner = String(matchWinner).toLowerCase() === selectedPlayerSide;
                const isTie = !matchWinner || matchWinner === 'tie' || matchWinner === 'empate';

                let bannerBg = '#dc3545'; // Derrota
                let bannerText = '¡PERDISTE!';
                let bannerSub = '¡Buen intento! Tu rival completó la baraja más rápido.';

                if (isTie) {
                    bannerBg = '#0dcaf0'; // Empate
                    bannerText = '¡EMPATE!';
                    bannerSub = 'Ambos jugadores igualaron el marcador.';
                } else if (isMeWinner) {
                    bannerBg = '#198754'; // Ganaste
                    bannerText = '¡GANASTE!';
                    bannerSub = winningReason === 'all_pairs_matched'
                        ? '¡Completaste todos los pares de la baraja!'
                        : '¡Obtuviste la mayor cantidad de aciertos!';
                }

                return (
                    <main className="memory-fan__results">
                        <div
                            className="memory-fan__results-banner"
                            style={{ backgroundColor: bannerBg }}
                        >
                            <h1 className="memory-fan__results-title">{bannerText}</h1>
                            <p className="memory-fan__results-subtitle">{bannerSub}</p>
                        </div>

                        {/* Avatar de resultado */}
                        <div className="memory-fan__results-avatar-wrap">
                            {isMeWinner && myWinnerAvatar ? (
                                <img src={myWinnerAvatar} alt="Ganador" className="memory-fan__results-avatar" />
                            ) : myTeamAvatar ? (
                                <img src={myTeamAvatar} alt="Avatar" className="memory-fan__results-avatar" />
                            ) : (
                                <div className="memory-fan__results-avatar-placeholder" style={{ backgroundColor: myTeamColor }}>
                                    <i className={`bi ${isMeWinner ? 'bi-trophy-fill' : 'bi-emoji-smile-fill'} display-4 text-dark`}></i>
                                </div>
                            )}
                        </div>

                        {/* Tarjeta de métricas finales */}
                        {/* <div className="memory-fan__results-metrics">
                            <div className="memory-fan__results-row">
                                <span className="memory-fan__metrics-label">Tus Pares Encontrados:</span>
                                <span className="memory-fan__metrics-val" style={{ color: myTeamColor }}>{myScore}</span>
                            </div>
                            <div className="memory-fan__results-row">
                                <span className="memory-fan__metrics-label">Pares del Rival:</span>
                                <span className="memory-fan__metrics-val text-white-50">{rivalScore}</span>
                            </div>
                            <div className="memory-fan__results-row">
                                <span className="memory-fan__metrics-label">Tus Movimientos:</span>
                                <span className="memory-fan__metrics-val text-white">{myTotalFlips}</span>
                            </div>
                            <div className="memory-fan__results-row">
                                <span className="memory-fan__metrics-label">Movimientos del Rival:</span>
                                <span className="memory-fan__metrics-val text-white-50">{rivalTotalFlips}</span>
                            </div>
                        </div> */}

                        {/* Botones de acción */}
                        {/* <div className="memory-fan__results-actions">
                            <button
                                type="button"
                                className="Btn__Adsl--Red w-100 py-2 mb-2"
                                onClick={handleLeaveGame}
                            >
                                Cambiar de Personaje
                            </button>
                        </div> */}
                    </main>
                );
            })()}

            {/* Créditos de pie de página */}
            <footer className="memory-fan__footer">
                <p className="FooterCredits" style={{ color: '#ffffff', fontSize: '20px' }}>Hecho con ♥ por Interaktia</p>
            </footer>
        </div>
    );
};

export const MemoryFan = MemoryPlayer;
export default MemoryPlayer;
